import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "space-y-4 p-4 sm:p-6 lg:p-8"
}
const _hoisted_2 = { class: "flex flex-wrap items-center justify-between gap-3" }
const _hoisted_3 = { class: "flex flex-wrap gap-3" }
const _hoisted_4 = { class: "space-y-6 rounded-lg bg-white p-4 shadow sm:p-6" }
const _hoisted_5 = {
  key: 0,
  class: "px-4 py-2"
}
const _hoisted_6 = ["href"]

import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { ExternalLinkIcon } from '@heroicons/vue/solid';
import { MetaIntegrationService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';
import { useUiStore } from '@/stores';
import {
    MetaConnectionStatusResult,
    MetaPhoneNumber,
    MetaWhatsAppNumberPolicy,
    MetaWhatsAppNumberPolicyPhone,
    WhatsAppWabaStatusColorMap,
} from '@/types';
import CompanyPhoneQuotaSummary from './components/CompanyPhoneQuotaSummary.vue';
import WhatsAppMetaHeader from './components/WhatsAppMetaHeader.vue';
import WhatsAppMetaConfirmDialog from './components/WhatsAppMetaConfirmDialog.vue';
import WhatsAppMetaPhoneActivationDialog from './components/WhatsAppMetaPhoneActivationDialog.vue';
import WhatsAppMetaPhoneNumberListItem from './components/WhatsAppMetaPhoneNumberListItem.vue';
import { attendanceUrl } from '@/configs';
import { wabaStatus } from '@/utils/whatsappMeta';

const perPage = 10;


export default /*@__PURE__*/_defineComponent({
  __name: 'WhatsAppMetaDetailPage',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const { locale, t } = useI18n();
const uiStore = useUiStore();
const waba = ref<MetaConnectionStatusResult | null>(null);
const policy = ref<MetaWhatsAppNumberPolicy | null>(null);
const busy = ref(false);
const search = ref('');
const phonePage = ref(1);
const phoneToActivate = ref<MetaPhoneNumber | null>(null);
const isDisconnectOpen = ref(false);
const phoneToDisable = ref<MetaWhatsAppNumberPolicyPhone | null>(null);
const wabaId = computed(() => String(route.params.wabaId));
const metaManagerUrl = computed(
    () =>
        `https://business.facebook.com/latest/whatsapp_manager/phone_numbers/?business_id=${waba.value?.businessId || ''}`,
);
const externalLinks = computed(() =>
    [
        { href: metaManagerUrl.value, label: t('whatsappMeta.detail.manageNumbersInMeta') },
        { href: attendanceUrl, label: t('whatsappMeta.list.openAttendance') },
    ].filter((link) => link.href),
);
const setLoading = (value: boolean) => uiStore.setIsLoading(value);
const digits = (value: string) => value.replace(/\D/g, '');
const emptyPolicy = (): MetaWhatsAppNumberPolicy => ({
    version: 0,
    used: 0,
    limit: null,
    full: false,
    phones: [],
});
const hasMoreThanTenPhones = computed(() => (waba.value?.phoneNumbers.length || 0) > perPage);
const isQuotaAvailable = computed(() => !policy.value?.full || policy.value.limit === null);
const shouldPrepareIntegration = computed(
    () =>
        Boolean(waba.value) &&
        !waba.value?.disconnectedAt &&
        (!waba.value?.systemUserAssigned || !waba.value?.webhookSubscribed),
);

const filteredPhones = computed(() => {
    if (!waba.value) return [];

    const value = search.value.trim().toLocaleLowerCase(locale.value);
    if (!value) return waba.value.phoneNumbers;

    const numeric = digits(value);
    return waba.value.phoneNumbers.filter((phone) =>
        [phone.displayPhoneNumber, phone.verifiedName, phone.phoneNumberId].some(
            (item) =>
                item &&
                (item.toLocaleLowerCase(locale.value).includes(value) ||
                    (numeric && digits(item).includes(numeric))),
        ),
    );
});

const pagePhones = computed(() => {
    if (!hasMoreThanTenPhones.value) return filteredPhones.value;
    return filteredPhones.value.slice((phonePage.value - 1) * perPage, phonePage.value * perPage);
});

const phonePolicy = (phone: MetaPhoneNumber): MetaWhatsAppNumberPolicyPhone | null =>
    policy.value?.phones.find(
        (item) => item.wabaId === wabaId.value && item.phoneNumberId === phone.phoneNumberId,
    ) ?? null;

const status = computed(() => (waba.value ? wabaStatus(waba.value) : null));

const load = async () => {
    setLoading(true);
    try {
        const wabaResponse = await MetaIntegrationService.getWaba(wabaId.value);
        waba.value = wabaResponse.data;
        policy.value = waba.value.disconnectedAt
            ? emptyPolicy()
            : (await MetaIntegrationService.getWhatsAppNumberPolicy()).data;
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const prepareIntegration = async () => {
    if (busy.value) return;

    busy.value = true;
    setLoading(true);
    try {
        waba.value = (await MetaIntegrationService.activateWaba(wabaId.value)).data;
        toast.success(t('whatsappMeta.detail.integrationPrepared'));
        await load();
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
        busy.value = false;
    }
};

const synchronize = async () => {
    if (busy.value) return;

    busy.value = true;
    setLoading(true);
    try {
        waba.value = (await MetaIntegrationService.synchronizeWaba(wabaId.value)).data;
        policy.value = (await MetaIntegrationService.getWhatsAppNumberPolicy()).data;
        toast.success(t('whatsappMeta.detail.numbersSynchronized'));
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
        busy.value = false;
    }
};

const activate = async (pin: string) => {
    if (!phoneToActivate.value?.phoneNumberId || !policy.value || busy.value) return;

    busy.value = true;
    setLoading(true);
    try {
        waba.value = (
            await MetaIntegrationService.activatePhoneNumber(
                wabaId.value,
                phoneToActivate.value.phoneNumberId,
                pin,
                policy.value.version,
            )
        ).data;
        phoneToActivate.value = null;
        await load();
        toast.success(t('whatsappMeta.detail.numberActivated'));
    } catch (error) {
        toastServiceError(error);
        await load();
    } finally {
        setLoading(false);
        busy.value = false;
    }
};

const setEnabled = async (phone: MetaWhatsAppNumberPolicyPhone, enabled: boolean) => {
    if (!policy.value || busy.value) return;

    busy.value = true;
    setLoading(true);
    try {
        await MetaIntegrationService.setWhatsAppPhoneApproval(
            wabaId.value,
            phone.id,
            enabled,
            policy.value.version,
        );
        policy.value = (await MetaIntegrationService.getWhatsAppNumberPolicy()).data;
        toast.success(t(enabled ? 'whatsappMeta.quota.enabled' : 'whatsappMeta.quota.disabled'));
    } catch (error) {
        toastServiceError(error);
        await load();
    } finally {
        setLoading(false);
        busy.value = false;
    }
};

const requestEnabled = (phone: MetaWhatsAppNumberPolicyPhone, enabled: boolean) => {
    if (enabled) return setEnabled(phone, true);
    phoneToDisable.value = phone;
};

const confirmDisable = async () => {
    if (!phoneToDisable.value) return;
    await setEnabled(phoneToDisable.value, false);
    phoneToDisable.value = null;
};

const disconnect = async () => {
    if (busy.value) return;

    busy.value = true;
    setLoading(true);
    try {
        await MetaIntegrationService.disconnectWaba(wabaId.value);
        toast.success(t('whatsappMeta.detail.disconnected'));
        isDisconnectOpen.value = false;
        router.push({ name: 'ListWhatsAppMeta' });
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
        busy.value = false;
    }
};

watch(search, () => (phonePage.value = 1));
onMounted(load);

return (_ctx: any,_cache: any) => {
  const _component_UiButton = _resolveComponent("UiButton")!
  const _component_UiBadge = _resolveComponent("UiBadge")!
  const _component_UiListSearch = _resolveComponent("UiListSearch")!
  const _component_UiSettingsRow = _resolveComponent("UiSettingsRow")!
  const _component_UiSettingsSection = _resolveComponent("UiSettingsSection")!
  const _component_UiPagination = _resolveComponent("UiPagination")!

  return (waba.value && policy.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_UiButton, {
            variant: "white",
            "text-variant": "primary",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(router).push({ name: 'ListWhatsAppMeta' })))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)('whatsappMeta.detail.back')), 1)
            ]),
            _: 1
          }),
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_component_UiButton, {
              variant: "white",
              "text-variant": "primary",
              disabled: busy.value || Boolean(waba.value.disconnectedAt),
              onClick: synchronize
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('whatsappMeta.detail.synchronizeNumbers')), 1)
              ]),
              _: 1
            }, 8, ["disabled"]),
            (shouldPrepareIntegration.value)
              ? (_openBlock(), _createBlock(_component_UiButton, {
                  key: 0,
                  variant: "primary",
                  "text-variant": "white",
                  disabled: busy.value,
                  onClick: prepareIntegration
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)('whatsappMeta.detail.prepareIntegration')), 1)
                  ]),
                  _: 1
                }, 8, ["disabled"]))
              : _createCommentVNode("", true)
          ])
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(WhatsAppMetaHeader, {
            title: waba.value.displayName || waba.value.wabaId,
            subtitle: `WABA ${waba.value.wabaId}`,
            "status-label": status.value ? _unref(t)(`whatsappMeta.list.status.${status.value}`) : '',
            "status-variant": status.value ? _unref(WhatsAppWabaStatusColorMap)[status.value] : undefined,
            summary: _unref(t)('whatsappMeta.detail.numbersInWaba', waba.value.phoneNumbers.length)
          }, null, 8, ["title", "subtitle", "status-label", "status-variant", "summary"]),
          _createVNode(CompanyPhoneQuotaSummary, {
            used: policy.value.used,
            limit: policy.value.limit,
            full: policy.value.full
          }, null, 8, ["used", "limit", "full"]),
          _createVNode(_component_UiSettingsSection, {
            title: _unref(t)('whatsappMeta.detail.numbers'),
            footer: _unref(t)('whatsappMeta.detail.numbersFooter')
          }, {
            "header-action": _withCtx(() => [
              _createVNode(_component_UiBadge, {
                variant: "gray-100",
                "text-variant": "gray-700",
                size: "sm"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('whatsappMeta.detail.numberCount', waba.value.phoneNumbers.length)), 1)
                ]),
                _: 1
              })
            ]),
            default: _withCtx(() => [
              (hasMoreThanTenPhones.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    _createVNode(_component_UiListSearch, {
                      modelValue: search.value,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((search).value = $event)),
                      placeholder: _unref(t)('whatsappMeta.detail.searchNumber'),
                      class: "flex-1"
                    }, null, 8, ["modelValue", "placeholder"])
                  ]))
                : _createCommentVNode("", true),
              (waba.value.phoneNumbers.length === 0)
                ? (_openBlock(), _createBlock(_component_UiSettingsRow, {
                    key: 1,
                    title: _unref(t)('whatsappMeta.detail.noNumbersInWaba'),
                    description: _unref(t)('whatsappMeta.detail.noNumbersGuidance')
                  }, null, 8, ["title", "description"]))
                : (filteredPhones.value.length)
                  ? (_openBlock(true), _createElementBlock(_Fragment, { key: 2 }, _renderList(pagePhones.value, (phone) => {
                      return (_openBlock(), _createBlock(WhatsAppMetaPhoneNumberListItem, {
                        key: phone.phoneNumberId,
                        phone: phone,
                        "policy-phone": phonePolicy(phone),
                        "can-enable": isQuotaAvailable.value,
                        onActivate: 
                            (id) =>
                                (phoneToActivate.value =
                                    waba.value?.phoneNumbers.find(
                                        (phone) => phone.phoneNumberId === id,
                                    ) || null)
                        ,
                        onApproval: requestEnabled
                      }, null, 8, ["phone", "policy-phone", "can-enable", "onActivate"]))
                    }), 128))
                  : (_openBlock(), _createBlock(_component_UiSettingsRow, {
                      key: 3,
                      title: _unref(t)('whatsappMeta.detail.noNumbersFound')
                    }, null, 8, ["title"]))
            ]),
            _: 1
          }, 8, ["title", "footer"]),
          (hasMoreThanTenPhones.value && filteredPhones.value.length)
            ? (_openBlock(), _createBlock(_component_UiPagination, {
                key: 0,
                modelValue: phonePage.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((phonePage).value = $event)),
                count: filteredPhones.value.length,
                "per-page": perPage
              }, null, 8, ["modelValue", "count"]))
            : _createCommentVNode("", true),
          _createVNode(_component_UiSettingsSection, {
            title: _unref(t)('whatsappMeta.detail.links')
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(externalLinks.value, (link) => {
                return (_openBlock(), _createElementBlock("a", {
                  key: link.href,
                  class: "flex min-h-[52px] items-center justify-between p-4 text-sm font-medium text-gray-900 hover:bg-gray-50/70",
                  href: link.href,
                  target: "_blank",
                  rel: "noopener noreferrer"
                }, [
                  _createTextVNode(_toDisplayString(link.label) + " ", 1),
                  _createVNode(_unref(ExternalLinkIcon), {
                    class: "h-4 w-4 text-gray-400",
                    "aria-hidden": "true"
                  })
                ], 8, _hoisted_6))
              }), 128))
            ]),
            _: 1
          }, 8, ["title"]),
          _createVNode(_component_UiSettingsSection, {
            title: _unref(t)('whatsappMeta.detail.connection'),
            variant: "danger"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_UiSettingsRow, {
                title: _unref(t)('whatsappMeta.detail.disconnectWaba'),
                description: _unref(t)('whatsappMeta.detail.disconnectFootnote')
              }, {
                action: _withCtx(() => [
                  _createVNode(_component_UiButton, {
                    variant: "danger",
                    "text-variant": "white",
                    disabled: busy.value || Boolean(waba.value.disconnectedAt),
                    onClick: _cache[3] || (_cache[3] = ($event: any) => (isDisconnectOpen.value = true))
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(t)('whatsappMeta.detail.disconnectWaba')), 1)
                    ]),
                    _: 1
                  }, 8, ["disabled"])
                ]),
                _: 1
              }, 8, ["title", "description"])
            ]),
            _: 1
          }, 8, ["title"])
        ]),
        _createVNode(WhatsAppMetaPhoneActivationDialog, {
          phone: phoneToActivate.value,
          "is-submitting": busy.value,
          onClose: _cache[4] || (_cache[4] = ($event: any) => (phoneToActivate.value = null)),
          onConfirm: activate
        }, null, 8, ["phone", "is-submitting"]),
        _createVNode(WhatsAppMetaConfirmDialog, {
          open: phoneToDisable.value !== null,
          title: _unref(t)('whatsappMeta.disableDialog.title'),
          description: 
                _unref(t)('whatsappMeta.disableDialog.description', {
                    phone: phoneToDisable.value?.displayPhoneNumber || '',
                })
            ,
          "confirm-label": _unref(t)('whatsappMeta.disableDialog.confirm'),
          "is-submitting": busy.value,
          onClose: _cache[5] || (_cache[5] = ($event: any) => (phoneToDisable.value = null)),
          onConfirm: confirmDisable
        }, null, 8, ["open", "title", "description", "confirm-label", "is-submitting"]),
        _createVNode(WhatsAppMetaConfirmDialog, {
          open: isDisconnectOpen.value,
          title: _unref(t)('whatsappMeta.disconnectDialog.title'),
          description: 
                _unref(t)('whatsappMeta.disconnectDialog.description', {
                    waba: waba.value.displayName || waba.value.wabaId,
                })
            ,
          "confirm-label": _unref(t)('whatsappMeta.disconnectDialog.confirm'),
          "is-submitting": busy.value,
          onClose: _cache[6] || (_cache[6] = ($event: any) => (isDisconnectOpen.value = false)),
          onConfirm: disconnect
        }, null, 8, ["open", "title", "description", "confirm-label", "is-submitting"])
      ]))
    : _createCommentVNode("", true)
}
}

})